import {
  DateRangePicker as NextUIDateRangePicker,
  DateRangePickerProps as NextUIDateRangePickerProps,
} from "@heroui/react";
import {
  getCalendarClassNames,
  getDatePickerClassNames,
} from "ds/utils/styles";
import { CalendarIcon } from "lucide-react";
import {
  convertCalendarRangeToUnixRange,
  toCalendarDate,
  toHeroUIDateProp,
  CalendarDateInput,
} from "../../utils/date";
import { DateClearButton } from "../date-input/date-clear-button";

type Color = Extract<NextUIDateRangePickerProps["color"], "default">;
type LabelPlacement = Extract<
  NextUIDateRangePickerProps["labelPlacement"],
  "outside"
>;
type Variant = Extract<NextUIDateRangePickerProps["variant"], "bordered">;

export type DateRangePickerProps = Omit<
  NextUIDateRangePickerProps,
  | "color"
  | "labelPlacement"
  | "maxValue"
  | "minValue"
  | "selectorButtonPlacement"
  | "selectorIcon"
  | "value"
  | "variant"
> & {
  color?: Color;
  isClearable?: boolean;
  labelPlacement?: LabelPlacement;
  maxValue?: number;
  minValue?: number;
  name?: string;
  ref?: React.Ref<HTMLDivElement>;
  onChange?: (value: { end: number | null; start: number | null }) => void;
  value?: {
    end: number | null;
    start: number | null;
  };
  variant?: Variant;
};

export function DateRangePicker({ ref, ...props }: DateRangePickerProps) {
  const {
    granularity,
    isClearable = true,
    maxValue,
    minValue,
    onChange,
    value,
    ...rest
  } = props;

  const calendarValue = value
    ? {
        end: toCalendarDate(value.end, granularity),
        start: toCalendarDate(value.start, granularity),
      }
    : null;

  const handleChange = (
    range: {
      end: CalendarDateInput;
      start: CalendarDateInput;
    } | null,
  ) => {
    if (!onChange) return;
    onChange(convertCalendarRangeToUnixRange(range, granularity));
  };

  const handleClear = () => {
    onChange?.({ end: null, start: null });
  };

  const showClearButton =
    isClearable &&
    (value?.start != null || value?.end != null) &&
    !rest.isDisabled &&
    !rest.isReadOnly;

  return (
    <NextUIDateRangePicker
      {...rest}
      ref={ref}
      calendarProps={{
        classNames: getCalendarClassNames({ isRange: true }),
        visibleMonths: 2,
        weekdayStyle: "short",
      }}
      classNames={getDatePickerClassNames(props.size)}
      color="default"
      data-testid={props.name}
      labelPlacement="outside"
      maxValue={toHeroUIDateProp<NextUIDateRangePickerProps["maxValue"]>(
        maxValue,
        granularity,
      )}
      minValue={toHeroUIDateProp<NextUIDateRangePickerProps["minValue"]>(
        minValue,
        granularity,
      )}
      selectorButtonPlacement="end"
      selectorIcon={<CalendarIcon size={16} />}
      startContent={
        showClearButton ? (
          <DateClearButton
            id={props.name ? `${props.name}-clear` : "date-range-picker-clear"}
            onClear={handleClear}
          />
        ) : undefined
      }
      value={calendarValue as NextUIDateRangePickerProps["value"]}
      variant="bordered"
      onChange={handleChange}
    />
  );
}

DateRangePicker.displayName = "RecareUI.DateRangePicker";
